import { supabase } from './supabase';
import type { NLPModule, NLPPracticeStyle, NLPDifficulty, NLPSessionMode, MiltonPatternFocus, MiltonContext, MiltonFeedbackTiming } from '../components/ai/nlpPracticeConstants';

export interface NLPPracticeConfig {
  id: string;
  partner_id: string;
  vector_store_id: string | null;
  is_enabled: boolean;
  system_instructions_meta_model: string;
  system_instructions_presuppositions: string;
  system_instructions_milton_model: string;
  system_instructions_nlp_trainer: string;
  has_openai_key: boolean;
}

export interface NLPPracticeSession {
  id: string;
  coach_id: string;
  partner_id: string;
  module: NLPModule;
  practice_style: NLPPracticeStyle;
  difficulty: NLPDifficulty | null;
  mode: NLPSessionMode;
  duration_seconds: number;
  score: number | null;
  feedback_summary: string | null;
  status: 'in_progress' | 'completed' | 'abandoned';
  created_at: string;
  updated_at: string;
}

export interface NLPPracticeMessage {
  id: string;
  session_id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

export interface NLPPracticeFile {
  id: string;
  file_id: string;
  filename: string;
  bytes: number;
  status: string;
  created_at: number;
}

export interface NLPCoachAccess {
  id: string;
  partner_id: string;
  coach_id: string;
  is_enabled: boolean;
  created_at: string;
  coach_name?: string;
  coach_email?: string;
}

// ============================================================
// Config functions (partner)
// ============================================================

export async function fetchNLPPracticeConfig(partnerId: string): Promise<NLPPracticeConfig | null> {
  const { data, error } = await supabase
    .from('nlp_practice_configs')
    .select('id, partner_id, vector_store_id, is_enabled, system_instructions_meta_model, system_instructions_presuppositions, system_instructions_milton_model, system_instructions_nlp_trainer')
    .eq('partner_id', partnerId)
    .maybeSingle();

  if (error || !data) return null;

  const { data: hasKey } = await supabase.rpc('get_nlp_practice_key_status', { p_partner_id: partnerId });

  return {
    ...(data as unknown as Record<string, unknown>),
    has_openai_key: hasKey || false,
  } as NLPPracticeConfig;
}

export async function saveNLPPracticeConfig(
  partnerId: string,
  updates: Partial<Pick<NLPPracticeConfig, 'is_enabled' | 'system_instructions_meta_model' | 'system_instructions_presuppositions' | 'system_instructions_milton_model' | 'system_instructions_nlp_trainer'>>,
): Promise<void> {
  const { error } = await supabase
    .from('nlp_practice_configs')
    .upsert({
      partner_id: partnerId,
      ...updates,
      updated_at: new Date().toISOString(),
    });

  if (error) throw error;
}

export async function saveNLPPracticeOpenAIKey(partnerId: string, apiKey: string): Promise<void> {
  const { error } = await supabase.rpc('save_nlp_practice_openai_key', {
    p_partner_id: partnerId,
    p_openai_api_key: apiKey,
  });
  if (error) throw error;
}

// ============================================================
// Coach access functions (partner)
// ============================================================

export async function fetchAuthorizedCoaches(partnerId: string): Promise<NLPCoachAccess[]> {
  const { data, error } = await supabase
    .from('nlp_practice_coach_access')
    .select(`
      id, partner_id, coach_id, is_enabled, created_at,
      coach:coaches!inner(user_id)
    `)
    .eq('partner_id', partnerId);

  if (error || !data) return [];

  const enriched = await Promise.all(
    data.map(async (row: Record<string, unknown>) => {
      const coachData = row.coach as Record<string, unknown>;
      const userId = coachData?.user_id as string;
      const { data: userData } = await supabase
        .from('users')
        .select('full_name, email')
        .eq('id', userId)
        .maybeSingle();
      return {
        id: row.id as string,
        partner_id: row.partner_id as string,
        coach_id: row.coach_id as string,
        is_enabled: row.is_enabled as boolean,
        created_at: row.created_at as string,
        coach_name: userData?.full_name || 'Unknown',
        coach_email: userData?.email || '',
      };
    }),
  );

  return enriched;
}

export async function authorizeCoach(partnerId: string, coachId: string): Promise<void> {
  const { error } = await supabase
    .from('nlp_practice_coach_access')
    .insert({ partner_id: partnerId, coach_id: coachId, is_enabled: true });
  if (error) throw error;
}

export async function toggleCoachAccess(accessId: string, isEnabled: boolean): Promise<void> {
  const { error } = await supabase
    .from('nlp_practice_coach_access')
    .update({ is_enabled: isEnabled })
    .eq('id', accessId);
  if (error) throw error;
}

export async function removeCoachAccess(accessId: string): Promise<void> {
  const { error } = await supabase
    .from('nlp_practice_coach_access')
    .delete()
    .eq('id', accessId);
  if (error) throw error;
}

// ============================================================
// Session functions (coach)
// ============================================================

export async function createNLPSession(
  coachId: string | null,
  partnerId: string,
  module: NLPModule,
  practiceStyle: NLPPracticeStyle,
  difficulty: NLPDifficulty | null,
  mode: NLPSessionMode,
  miltonOptions?: {
    patternFocus?: MiltonPatternFocus | null;
    practiceContext?: MiltonContext | null;
    feedbackTiming?: MiltonFeedbackTiming | null;
    targetDurationMinutes?: number | null;
  },
): Promise<string | null> {
  const { data, error } = await supabase
    .from('nlp_practice_sessions')
    .insert({
      coach_id: coachId,
      partner_id: partnerId,
      module,
      practice_style: practiceStyle,
      difficulty,
      mode,
      status: 'in_progress',
      pattern_focus: miltonOptions?.patternFocus || null,
      practice_context: miltonOptions?.practiceContext || null,
      feedback_timing: miltonOptions?.feedbackTiming || null,
      target_duration_minutes: miltonOptions?.targetDurationMinutes || null,
    })
    .select('id')
    .single();

  if (error) return null;
  return data.id;
}

export async function completeNLPSession(
  sessionId: string,
  durationSeconds: number,
  score: number | null,
  feedbackSummary: string | null,
): Promise<void> {
  await supabase
    .from('nlp_practice_sessions')
    .update({
      duration_seconds: durationSeconds,
      score,
      feedback_summary: feedbackSummary,
      status: 'completed',
      updated_at: new Date().toISOString(),
    })
    .eq('id', sessionId);
}

export async function fetchCoachSessions(coachId: string): Promise<NLPPracticeSession[]> {
  const { data, error } = await supabase
    .from('nlp_practice_sessions')
    .select('*')
    .eq('coach_id', coachId)
    .order('created_at', { ascending: false });
  if (error || !data) return [];
  return data as unknown as NLPPracticeSession[];
}

export async function fetchPartnerSessions(partnerId: string): Promise<NLPPracticeSession[]> {
  const { data, error } = await supabase
    .from('nlp_practice_sessions')
    .select('*')
    .eq('partner_id', partnerId)
    .order('created_at', { ascending: false });
  if (error || !data) return [];
  return data as unknown as NLPPracticeSession[];
}

// ============================================================
// Partner coach progress functions
// ============================================================

export interface CoachProgressSummary {
  coachId: string;
  coachName: string;
  coachEmail: string;
  totalSessions: number;
  completedSessions: number;
  inProgressSessions: number;
  totalPracticeMinutes: number;
  lastSessionDate: string | null;
  firstSessionDate: string | null;
  moduleBreakdown: {
    module: NLPModule;
    sessionCount: number;
    averageScore: number | null;
    highestScore: number | null;
    latestScore: number | null;
    isMilton: boolean;
  }[];
  averageScore: number | null;
  hasPracticed: boolean;
}

export async function fetchPartnerCoachProgress(partnerId: string): Promise<CoachProgressSummary[]> {
  const { data: coaches } = await supabase
    .from('coaches')
    .select('id, user_id')
    .eq('partner_id', partnerId);

  if (!coaches || coaches.length === 0) return [];

  const coachNameMap: Record<string, { name: string; email: string }> = {};
  await Promise.all(coaches.map(async (c) => {
    const { data: u } = await supabase
      .from('users')
      .select('full_name, email')
      .eq('id', c.user_id)
      .maybeSingle();
    coachNameMap[c.id] = { name: u?.full_name || 'Unknown', email: u?.email || '' };
  }));

  const { data: sessions } = await supabase
    .from('nlp_practice_sessions')
    .select('*')
    .eq('partner_id', partnerId)
    .order('created_at', { ascending: true });

  if (!sessions) return [];

  const coachMap: Record<string, NLPPracticeSession[]> = {};
  coaches.forEach(c => { coachMap[c.id] = []; });
  (sessions as unknown as NLPPracticeSession[]).forEach(s => {
    if (s.coach_id && coachMap[s.coach_id]) {
      coachMap[s.coach_id].push(s);
    }
  });

  const moduleLabels: Record<string, NLPModule> = {
    meta_model: 'meta_model',
    presuppositions: 'presuppositions',
    milton_model: 'milton_model',
    nlp_trainer: 'nlp_trainer',
  };

  return Object.entries(coachMap).map(([coachId, coachSessions]) => {
    const nameInfo = coachNameMap[coachId] || { name: 'Unknown', email: '' };
    const completed = coachSessions.filter(s => s.status === 'completed');
    const inProgress = coachSessions.filter(s => s.status === 'in_progress');
    const totalMinutes = coachSessions.reduce((sum, s) => sum + Math.floor(s.duration_seconds / 60), 0);

    const modules = Object.keys(moduleLabels).filter(m => m !== 'nlp_trainer');
    const moduleBreakdown = modules.map(m => {
      const moduleSessions = completed.filter(s => s.module === m);
      const scoredSessions = moduleSessions.filter(s => s.score !== null);
      const isMilton = m === 'milton_model';
      const scores = scoredSessions.map(s => s.score!);
      return {
        module: m as NLPModule,
        sessionCount: coachSessions.filter(s => s.module === m).length,
        averageScore: scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null,
        highestScore: scores.length > 0 ? Math.max(...scores) : null,
        latestScore: scoredSessions.length > 0 ? scoredSessions[scoredSessions.length - 1].score : null,
        isMilton,
      };
    });

    const allScored = completed.filter(s => s.score !== null).map(s => {
      const isMilton = s.module === 'milton_model';
      return isMilton ? (s.score! * 10) : s.score!;
    });

    return {
      coachId,
      coachName: nameInfo.name,
      coachEmail: nameInfo.email,
      totalSessions: coachSessions.length,
      completedSessions: completed.length,
      inProgressSessions: inProgress.length,
      totalPracticeMinutes: totalMinutes,
      lastSessionDate: coachSessions.length > 0 ? coachSessions[coachSessions.length - 1].created_at : null,
      firstSessionDate: coachSessions.length > 0 ? coachSessions[0].created_at : null,
      moduleBreakdown,
      averageScore: allScored.length > 0 ? Math.round(allScored.reduce((a, b) => a + b, 0) / allScored.length) : null,
      hasPracticed: coachSessions.length > 0,
    };
  }).sort((a, b) => {
    if (a.hasPracticed && !b.hasPracticed) return -1;
    if (!a.hasPracticed && b.hasPracticed) return 1;
    return b.totalSessions - a.totalSessions;
  });
}

export async function deleteNLPSession(sessionId: string): Promise<void> {
  await supabase
    .from('nlp_practice_sessions')
    .delete()
    .eq('id', sessionId);
}

export async function fetchSessionMessages(sessionId: string): Promise<NLPPracticeMessage[]> {
  const { data, error } = await supabase
    .from('nlp_practice_messages')
    .select('*')
    .eq('session_id', sessionId)
    .order('created_at', { ascending: true });
  if (error || !data) return [];
  return data as unknown as NLPPracticeMessage[];
}

// ============================================================
// Chat function (coach)
// ============================================================

export async function sendNLPPracticeMessage(
  message: string,
  conversationHistory: { role: 'user' | 'assistant'; content: string }[],
  module: NLPModule,
  practiceStyle: NLPPracticeStyle,
  difficulty: NLPDifficulty | null,
  sessionId: string | null,
  miltonOptions?: {
    patternFocus?: MiltonPatternFocus | null;
    practiceContext?: MiltonContext | null;
    feedbackTiming?: MiltonFeedbackTiming | null;
    targetDurationMinutes?: number | null;
  },
): Promise<{ response: string; usage: { input_tokens: number; output_tokens: number } }> {
  const { data, error } = await supabase.functions.invoke('nlp-practice-chat', {
    body: {
      message,
      conversation_history: conversationHistory,
      module,
      practice_style: practiceStyle,
      difficulty,
      session_id: sessionId,
      pattern_focus: miltonOptions?.patternFocus || null,
      practice_context: miltonOptions?.practiceContext || null,
      feedback_timing: miltonOptions?.feedbackTiming || null,
      target_duration_minutes: miltonOptions?.targetDurationMinutes || null,
    },
  });

  if (error) {
    const response = (error as { context?: Response }).context;
    if (response) {
      try {
        const body = await response.clone().json() as { error?: string };
        if (body.error) throw new Error(body.error);
      } catch (responseError) {
        if (responseError instanceof Error && responseError.message !== 'Unexpected end of JSON input') {
          throw responseError;
        }
      }
    }
    throw error;
  }

  if (!data || typeof data.response !== 'string' || !data.usage) {
    throw new Error('The practice coach returned an invalid response. Please try again.');
  }

  return data;
}

// ============================================================
// Voice token function (coach)
// ============================================================

export type NLPModelVariant = 'gpt-realtime-2.1' | 'gpt-live-1';

export async function getNLPPracticeVoiceToken(
  module: NLPModule,
  practiceStyle: NLPPracticeStyle,
  difficulty: NLPDifficulty | null,
  modelVariant?: NLPModelVariant,
  sdpOffer?: string,
  miltonOptions?: {
    patternFocus?: MiltonPatternFocus | null;
    practiceContext?: MiltonContext | null;
    feedbackTiming?: MiltonFeedbackTiming | null;
    targetDurationMinutes?: number | null;
  },
): Promise<{ client_secret: string; session_id: string; usage_log_id: string | null; model_variant: string; backend_model: string | null; reasoning_effort: string | null; answer_sdp: string | null }> {
  const { data, error } = await supabase.functions.invoke('nlp-practice-token', {
    body: {
      module,
      practice_style: practiceStyle,
      difficulty,
      model_variant: modelVariant,
      sdp_offer: sdpOffer,
      pattern_focus: miltonOptions?.patternFocus || null,
      practice_context: miltonOptions?.practiceContext || null,
      feedback_timing: miltonOptions?.feedbackTiming || null,
      target_duration_minutes: miltonOptions?.targetDurationMinutes || null,
    },
  });

  if (error) {
    const response = (error as { context?: Response }).context;
    if (response) {
      try {
        const body = await response.clone().json() as { error?: string };
        if (body.error) throw new Error(body.error);
      } catch (responseError) {
        if (responseError instanceof Error && responseError.message !== 'Unexpected end of JSON input') {
          throw responseError;
        }
      }
    }
    throw error;
  }
  return data;
}

// ============================================================
// Knowledge base functions (partner)
// ============================================================

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

async function kbInvoke(action: string, method: 'GET' | 'POST' = 'POST', body?: BodyInit): Promise<any> {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token;
  if (!accessToken) throw new Error('Not authenticated');

  const headers: Record<string, string> = {
    'X-KB-Action': action,
    'Authorization': `Bearer ${accessToken}`,
    'apikey': SUPABASE_ANON_KEY,
  };

  let fetchBody: BodyInit | undefined;
  if (body instanceof FormData) {
    fetchBody = body;
  } else if (body) {
    headers['Content-Type'] = 'application/json';
    fetchBody = body;
  }

  const res = await fetch(
    `${SUPABASE_URL}/functions/v1/nlp-practice-kb`,
    { method, headers, body: fetchBody },
  );

  const text = await res.text();
  let json: any;
  try { json = JSON.parse(text); } catch { json = { error: text }; }

  if (!res.ok) {
    throw new Error(json.error || `HTTP ${res.status}`);
  }
  return json;
}

export async function createVectorStore(): Promise<string> {
  const data = await kbInvoke('create-store', 'POST');
  return data.vector_store_id;
}

export async function uploadKnowledgeFile(file: File): Promise<void> {
  const formData = new FormData();
  formData.append('file', file);
  await kbInvoke('upload-file', 'POST', formData);
}

export async function listKnowledgeFiles(): Promise<NLPPracticeFile[]> {
  const data = await kbInvoke('list-files', 'GET');
  return data?.files || [];
}

export async function deleteKnowledgeFile(fileId: string): Promise<void> {
  await kbInvoke('delete-file', 'POST', JSON.stringify({ file_id: fileId }));
}

export async function setVectorStoreId(vectorStoreId: string): Promise<void> {
  await kbInvoke('set-vector-store-id', 'POST', JSON.stringify({ vector_store_id: vectorStoreId }));
}

// ============================================================
// Authorization check (coach)
// ============================================================

export async function fetchFirstEnabledNLPPracticeConfig(): Promise<{ partner_id: string } | null> {
  const { data, error } = await supabase
    .from('nlp_practice_configs')
    .select('partner_id')
    .eq('is_enabled', true)
    .limit(1)
    .maybeSingle();
  if (error || !data) return null;
  return { partner_id: data.partner_id };
}

export async function checkCoachNLPPracticeAccess(): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;
  const { data, error } = await supabase.rpc('is_coach_nlp_practice_authorized', {
    p_coach_user_id: user.id,
  });
  if (error) return false;
  return data || false;
}

export async function getCoachNLPPracticePartnerId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data, error } = await supabase.rpc('get_coach_nlp_practice_partner', {
    p_coach_user_id: user.id,
  });
  if (error || !data) return null;
  return data;
}
